/**
 * Shared logic for the research card (`webSearch`, `readUrl`, `readPdf`,
 * `readGithubRepo`, `readSkill`). Rendering lives in `research-card.tsx` (web)
 * and `research-card.native.tsx`.
 *
 * Sources are deliberately not part of the card: the Sources row under the
 * answer already derives them from each result's `sources[]`
 * (`streaming-sources.ts`), so listing them here would say it twice.
 */
import type { ResearchUrlStatus } from '@tabbio/types';

export type ResearchCardKind = 'search' | 'url' | 'pdf' | 'github' | 'skill';

export const RESEARCH_TOOL_KINDS: Readonly<Record<string, ResearchCardKind>> = {
  webSearch: 'search',
  readUrl: 'url',
  readPdf: 'pdf',
  readGithubRepo: 'github',
  readSkill: 'skill',
};

export type BriefBlock =
  | { type: 'paragraph'; text: string }
  | { type: 'bullets'; items: string[] };

export type ResearchUrlRow = {
  url: string;
  label: string;
  status: ResearchUrlStatus;
};

export type ResearchCardModel = {
  kind: ResearchCardKind;
  blocks: BriefBlock[];
  queries: string[];
  urls: ResearchUrlRow[];
  repository: {
    name: string;
    sha: string;
    filesRead: number;
    treeTruncated: boolean;
  } | null;
  skill: { name: string; version: string | null } | null;
  /** The first source's publisher domain (or title), naming a PDF read. */
  sourceName: string | null;
};

/** How much of the brief shows before "Show more" (characters of prose). */
export const BRIEF_PREVIEW_CHARS = 420;

const URL_STATUSES: readonly ResearchUrlStatus[] = ['success', 'error', 'paywall', 'unsafe'];
const MAX_QUERIES = 6;

function record(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/** Links keep their text, emphasis and code marks go, whitespace collapses. */
export function stripInlineMarkdown(text: string): string {
  return text
    .replace(/!\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/(\*\*|__)(.+?)\1/g, '$2')
    .replace(/\*(\S(?:.*?\S)?)\*/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Markdown-lite: paragraphs and bullet lists only. Headings become their own
 * paragraph, quotes lose the marker, tables, rules and code fences are dropped.
 * The brief is a compact summary above the real answer, not a document.
 */
export function parseBriefText(text: string): BriefBlock[] {
  const blocks: BriefBlock[] = [];
  let paragraph: string[] = [];
  let bullets: string[] = [];

  const flushParagraph = () => {
    if (!paragraph.length) return;
    const joined = stripInlineMarkdown(paragraph.join(' '));
    if (joined) blocks.push({ type: 'paragraph', text: joined });
    paragraph = [];
  };
  const flushBullets = () => {
    if (!bullets.length) return;
    blocks.push({ type: 'bullets', items: bullets });
    bullets = [];
  };

  for (const raw of text.replace(/\r\n?/g, '\n').split('\n')) {
    const line = raw.trim();
    if (!line) {
      flushParagraph();
      flushBullets();
      continue;
    }
    if (/^```/.test(line) || /^\|/.test(line) || /^([-*_=])\1{2,}$/.test(line)) continue;

    const bullet = line.match(/^(?:[-*+•]|\d{1,3}[.)])\s+(.*)$/);
    if (bullet) {
      flushParagraph();
      const item = stripInlineMarkdown(bullet[1] ?? '');
      if (item) bullets.push(item);
      continue;
    }

    flushBullets();
    const heading = line.match(/^#{1,6}\s+(.*)$/);
    if (heading) {
      flushParagraph();
      paragraph.push(heading[1] ?? '');
      flushParagraph();
      continue;
    }
    const quote = line.match(/^>\s?(.*)$/);
    paragraph.push(quote ? quote[1] ?? '' : line);
  }

  flushParagraph();
  flushBullets();
  return blocks;
}

/** The first `maxChars` of prose, cut at block or bullet boundaries. */
export function collapseBrief(
  blocks: BriefBlock[],
  maxChars: number,
): { blocks: BriefBlock[]; truncated: boolean } {
  const out: BriefBlock[] = [];
  let used = 0;
  for (const block of blocks) {
    if (used >= maxChars) return { blocks: out, truncated: true };
    if (block.type === 'paragraph') {
      out.push(block);
      used += block.text.length;
      continue;
    }
    const items: string[] = [];
    for (const item of block.items) {
      if (used >= maxChars && items.length > 0) {
        out.push({ type: 'bullets', items });
        return { blocks: out, truncated: true };
      }
      items.push(item);
      used += item.length;
    }
    out.push({ type: 'bullets', items });
  }
  return { blocks: out, truncated: false };
}

function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function readUrls(value: unknown): ResearchUrlRow[] {
  if (!Array.isArray(value)) return [];
  const rows: ResearchUrlRow[] = [];
  for (const item of value) {
    const entry = record(item);
    if (!entry || typeof entry.url !== 'string' || !entry.url) continue;
    const status = (URL_STATUSES as readonly unknown[]).includes(entry.status)
      ? (entry.status as ResearchUrlStatus)
      : 'error';
    rows.push({ url: entry.url, label: hostLabel(entry.url), status });
  }
  return rows;
}

function readRepository(value: unknown): ResearchCardModel['repository'] {
  const repo = record(value);
  if (!repo || typeof repo.owner !== 'string' || typeof repo.repo !== 'string') return null;
  return {
    name: `${repo.owner}/${repo.repo}`,
    sha: typeof repo.sha === 'string' ? repo.sha.slice(0, 7) : '',
    filesRead: Array.isArray(repo.filesRead) ? repo.filesRead.length : 0,
    treeTruncated: repo.treeTruncated === true,
  };
}

function readSkill(value: unknown): ResearchCardModel['skill'] {
  const skill = record(value);
  if (!skill) return null;
  const name =
    typeof skill.name === 'string' && skill.name
      ? skill.name
      : typeof skill.skillId === 'string'
        ? skill.skillId
        : '';
  if (!name) return null;
  return { name, version: typeof skill.version === 'string' && skill.version ? skill.version : null };
}

function readSourceName(value: unknown): string | null {
  if (!Array.isArray(value)) return null;
  for (const item of value) {
    const entry = record(item);
    if (!entry) continue;
    for (const key of ['domain', 'title']) {
      const candidate = entry[key];
      if (typeof candidate === 'string' && candidate.trim()) return candidate.trim().replace(/^www\./, '');
    }
  }
  return null;
}

/**
 * Shape one research tool result. Returns null for anything with nothing to
 * show (an error envelope, an empty read), so the card renders nothing rather
 * than an empty box under a trace line that already says what happened.
 */
export function parseResearchResult(toolName: string, result: unknown): ResearchCardModel | null {
  const kind = RESEARCH_TOOL_KINDS[toolName];
  const source = record(result);
  if (!kind || !source || source.ok === false) return null;

  const text = typeof source.text === 'string' ? source.text : '';
  const queries = Array.isArray(source.queries)
    ? source.queries
        .filter((query): query is string => typeof query === 'string' && query.trim().length > 0)
        .map((query) => query.trim())
        .slice(0, MAX_QUERIES)
    : [];

  const model: ResearchCardModel = {
    kind,
    blocks: kind === 'skill' ? [] : parseBriefText(text),
    queries,
    urls: kind === 'url' ? readUrls(source.urls) : [],
    repository: kind === 'github' ? readRepository(source.repository) : null,
    skill: kind === 'skill' ? readSkill(source.skill) : null,
    sourceName: readSourceName(source.sources),
  };

  const empty =
    !model.blocks.length &&
    !model.queries.length &&
    !model.urls.length &&
    !model.repository &&
    !model.skill;
  return empty ? null : model;
}

// ── The activity line (Gemini draws what it did as one collapsible line) ───

/** Collapsed by default: the answer is the hero, the process is one line. */
export const RESEARCH_ACTIVITY_DEFAULT_OPEN = false;

export type ResearchActivityLabel =
  | { key: 'search' }
  | { key: 'read'; name: string };

/**
 * "Searched the web" for a search; "Read <domain or name>" for a read: the
 * pages' domains (two at most), the repository, the skill, or the PDF's
 * source. Falls back to the kind's title when nothing names it.
 */
export function researchActivityLabel(model: ResearchCardModel): ResearchActivityLabel | null {
  switch (model.kind) {
    case 'search':
      return { key: 'search' };
    case 'url': {
      const names = [...new Set(model.urls.map((row) => row.label))];
      return names.length ? { key: 'read', name: names.slice(0, 2).join(', ') } : null;
    }
    case 'github':
      return model.repository ? { key: 'read', name: model.repository.name } : null;
    case 'skill':
      return model.skill ? { key: 'read', name: model.skill.name } : null;
    case 'pdf':
      return model.sourceName ? { key: 'read', name: model.sourceName } : null;
  }
}

/** Whether the open activity box has anything beyond the line itself. */
export function researchActivityHasBody(model: ResearchCardModel): boolean {
  return Boolean(
    model.blocks.length || model.queries.length || model.urls.length || model.repository || model.skill,
  );
}
